import {
	createContext,
	FC,
	PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
	getAuth,
	isSignInWithEmailLink,
	signInWithEmailLink,
} from 'firebase/auth';

import {
	personalDetailsManager,
	useBusinessLayer,
	usePasswordlessLogin,
} from '@mopla/business-logic';
import { SuccessStep } from '@mopla/constants';

import { environment } from '../../../../environments/environment';
import { PasswordlessLoginModal } from '../../../organisms/LoginScreen/PasswordlessLoginModal';
import { useWelcomePageStoreContext } from '../useWelcomePageStore';
import { useSubmitForgotPassword } from '../utils/useSubmitForgotPassword';

export type IWithPasswordlessProps = PropsWithChildren;

interface IPasswordlessLoginContextValue {
	onPasswordlessLogin: (email: string) => Promise<void> | void;
}

const PasswordlessLoginContext = createContext<IPasswordlessLoginContextValue>(
	{} as IPasswordlessLoginContextValue
);

/** Warning: The component and the passwordless login feature was not tested after the refactoring was introduced
 * I'm not even sure if the passwordless login feature is used at all
 * */
export const WithPasswordless: FC<IWithPasswordlessProps> = ({ children }) => {
	const { dispatch, setIsLoading, setSuccessType, showSetPasswordModal } =
		useWelcomePageStoreContext();
	const navigate = useNavigate();
	const location = useLocation();
	const { api } = useBusinessLayer();
	const { onSubmitForgotPassword } = useSubmitForgotPassword();
	const passwordlessLogin = usePasswordlessLogin(environment.baseUrl);
	const [query] = useSearchParams();

	const submitSetPassword = async () => {
		const email = localStorage.getItem('emailForSignIn');
		if (email) {
			dispatch({ type: 'SHOW_PWD_MODAL', payload: false });

			await onSubmitForgotPassword({ email });

			navigate({
				pathname: '/welcome',
				search: 'forgotPassword',
			});
		}
	};

	const rejectSetPassword = () => {
		dispatch({ type: 'SHOW_PWD_MODAL', payload: false });
		setIsLoading(true);

		api
			.get('/api/whoami')
			.then((user) => {
				navigate(location.pathname, { replace: true });
				if (user.pleaseComplete) {
					return navigate({ pathname: '/welcome', search: 'personalDetails' });
				}

				personalDetailsManager.set('completed');
				return !Capacitor.isNativePlatform()
					? navigate({ pathname: '/welcome', search: 'tryOurApp' })
					: navigate('/home');
			})
			.catch(() => {
				return navigate({ pathname: '/welcome', search: 'personalDetails' });
			})
			.finally(() => {
				localStorage.removeItem('emailForSignIn');
				setIsLoading(false);
			});
	};

	const onPasswordlessLogin = useCallback(
		async (email: string) => {
			setIsLoading(true);

			try {
				const res = await passwordlessLogin(email, 'NORMAL');

				if (res.status === 204) {
					console.log(
						'successfully login -> open "PW login easy" screen + maybe link '
					);
					localStorage.setItem('emailForSignIn', email);
					localStorage.setItem('emailSendTime', String(new Date().valueOf()));
					setSuccessType(SuccessStep.pwlessLoginEmailSent);
				}
			} catch (e) {
				console.error('Error on passwordless login: ', e);
			} finally {
				setIsLoading(false);
			}
		},
		[passwordlessLogin, setIsLoading, setSuccessType]
	);

	useEffect(() => {
		const auth = getAuth();
		const saved_email = localStorage.getItem('emailForSignIn');

		if (!!saved_email && isSignInWithEmailLink(auth, window.location.href)) {
			signInWithEmailLink(auth, saved_email, window.location.href)
				.then((res) => {
					res.user.getIdTokenResult().then(() => {
						dispatch({ type: 'SHOW_PWD_MODAL', payload: true });
						navigate('/welcome', { replace: true });
					});
				})
				.catch((error) => {
					navigate('/welcome', { replace: true });
					console.error(error, 'Invalid link or oobCode');
				});
		}
	}, [dispatch, location.pathname, navigate, query]);

	const ctx = useMemo<IPasswordlessLoginContextValue>(
		() => ({ onPasswordlessLogin }),
		[onPasswordlessLogin]
	);

	return (
		<PasswordlessLoginContext.Provider value={ctx}>
			{children}
			<PasswordlessLoginModal
				open={showSetPasswordModal}
				onSubmit={submitSetPassword}
				onCancel={rejectSetPassword}
			/>
		</PasswordlessLoginContext.Provider>
	);
};

export function usePasswordlessLoginContext() {
	const ctx = useContext(PasswordlessLoginContext);
	if (!ctx) {
		throw new Error(
			'usePasswordlessLogin must be used within PasswordlessLoginProvider'
		);
	}
	return ctx;
}
